import { type BizcycleProject, BusinessSteps, type JiraIssue, routes } from '@pmai/contracts';
import { Button, Card, InlineCheckbox } from '@pmai/ui';
import { useTranslation } from 'react-i18next';
import { JiraKey, useJiraBaseUrl } from '../../entities/jira/jira-links';
import { useApiMutation } from '../../shared/api/hooks';
import { useEnumLabel } from '../../shared/i18n';
import { stepOf } from './bizcycle-model';
import { IssueControls, useIssueUpdate } from './issue-controls';

/**
 * "Jauni uzdevumi apstiprināšanai (N)" — tasks not yet approved, grouped by step in wheel order;
 * "Apstiprināt visus" puts a group onto the wheel (FR-BIZ-007…009). Hidden when there are none.
 */
export function PendingApprovals({
  issues,
  projects,
  canEdit,
}: {
  issues: readonly JiraIssue[];
  projects: readonly BizcycleProject[];
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const label = useEnumLabel();
  const baseUrl = useJiraBaseUrl();
  const update = useIssueUpdate();
  const approve = useApiMutation(routes.bizcycle.approve, { invalidates: [routes.bizcycle.issues] });
  if (issues.length === 0) return null;
  const title = t('bizcycle.pendingTitle', { count: issues.length });
  return (
    <Card title={title}>
      <div className="flex flex-col gap-5">
        {BusinessSteps.options
          .map((o) => ({ o, group: issues.filter((i) => stepOf(i) === o.code) }))
          .filter(({ group }) => group.length > 0)
          .map(({ o, group }) => {
            const name = `${o.code} ${label(o)}`;
            return (
              <section key={o.code} role="group" aria-label={name} className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <h3 className="text-sm font-semibold">
                    {t('bizcycle.groupTitle', { step: name, count: group.length })}
                  </h3>
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={approve.isPending}
                      onClick={() => approve.mutate({ body: { keys: group.map((i) => i.key) } })}
                    >
                      {t('bizcycle.approveAll')}
                    </Button>
                  )}
                </div>
                <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
                  {group.map((i) => (
                    <li
                      key={i.key}
                      aria-label={i.key}
                      className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                    >
                      <span className="text-sm">
                        <span className="font-semibold">
                          <JiraKey jiraKey={i.key} baseUrl={baseUrl} />
                        </span>{' '}
                        {i.summary}{' '}
                        <i className="text-ink-muted">· {i.assigneeName ?? t('bizcycle.unassigned')}</i>
                      </span>
                      <span className="flex flex-wrap items-center gap-2">
                        <label className="flex items-center gap-1.5 text-sm">
                          <InlineCheckbox
                            label={t('bizcycle.burn', { key: i.key })}
                            checked={i.burning}
                            disabled={!canEdit}
                            onChange={(e) =>
                              update.mutate({ params: { key: i.key }, body: { burning: e.target.checked } })
                            }
                          />
                          {t('bizcycle.burning')}
                        </label>
                        <IssueControls issue={i} projects={projects} disabled={!canEdit} />
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
      </div>
    </Card>
  );
}
